import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams, makeUnique } from "@nens/shared-utils";

import { paginatedSchema } from "../pagination";

import { useBootstrap } from "./bootstrap";
import { useUserOrganisations, userSchema } from "./users";

export const organisationListItemSchema = z.object({
  url: z.string(),
  uuid: z.string(),
  name: z.string(),
});

export type OrganisationListItem = z.infer<typeof organisationListItemSchema>;

export const organisationListSchema = paginatedSchema(organisationListItemSchema);
export type OrganisationList = z.infer<typeof organisationListSchema>;

/* There is no difference between list view and detail view
   for organisations */
export const organisationSchema = organisationListItemSchema;
export type Organisation = OrganisationListItem;

export function useOrganisations(params = {}) {
  const url = combineUrlAndParams("/api/v4/organisations/", params);
  return useParsedQuery<OrganisationList>(
    ["lizard-api-v4", "organisations", url],
    () => fetchWithError(url),
    (json: unknown) => organisationListSchema.parse(json)
  );
}

export function useAllOrganisations(params = {}) {
  const url = combineUrlAndParams("/api/v4/organisations/?page_size=0", params);
  return useParsedQuery<Organisation[]>(
    ["lizard-api-v4", "organisations", url],
    () => fetchWithError(url),
    (json: unknown) => z.array(organisationSchema).parse(json)
  );
}

export function useMultipleOrganisations(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "organisations", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/organisations/${uuid}/`),
        parseFunction: (json: unknown) => organisationSchema.parse(json),
      };
    })
  );
}

export function useOrganisation(uuid: string) {
  return useParsedQuery<Organisation>(
    ["lizard-api-v4", "organisations", uuid],
    () => fetchWithError(`/api/v4/organisations/${uuid}/`),
    (json: unknown) => organisationSchema.parse(json)
  );
}

export function useRelevantOrganisations() {
  /* The portal defines a "whitelist" of organisations of which public
     data is visible. In addition to that, it's possible that the user
     is logged in and has roles in other organisations. These are the
     only relevant organisations, as the user isn't able to see data
     in any others. */
  /* Also, the portal may define a "default_organisation". We return it too.
     It can be the default choice.*/
  const bootstrapResponse = useBootstrap();
  const userOrganisationsResponse = useUserOrganisations(
    bootstrapResponse.isSuccess ? bootstrapResponse.data.user.id : null
  );

  const userOrganisations = userOrganisationsResponse.isSuccess
    ? userOrganisationsResponse.data.map((userOrganisation) => userOrganisation.uuid)
    : [];
  const whitelistOrganisations = bootstrapResponse.isSuccess
    ? bootstrapResponse.data.organisation_whitelist ?? []
    : [];

  const organisationUuids = makeUnique([...userOrganisations, ...whitelistOrganisations]);

  const allOrganisationsResponse = useAllOrganisations();
  const allOrganisations = allOrganisationsResponse.isSuccess ? allOrganisationsResponse.data : [];
  const organisations = allOrganisations.filter((organisation) =>
    organisationUuids.includes(organisation.uuid)
  );

  const defaultOrganisationUuid = bootstrapResponse.isSuccess
    ? bootstrapResponse.data.default_organisation
    : null;

  return {
    organisationsLoading: allOrganisationsResponse.isLoading,
    organisations,
    defaultOrganisationUuid:
      defaultOrganisationUuid !== null && organisationUuids.includes(defaultOrganisationUuid)
        ? defaultOrganisationUuid
        : null,
  };
}

/* /users/me */

export const userRoleSchema = z
  .literal("user")
  .or(z.literal("admin"))
  .or(z.literal("supplier"))
  .or(z.literal("manager"));
export type UserRole = z.infer<typeof userRoleSchema>;

export const organisationUserSchema = userSchema.extend({
  roles: z.array(userRoleSchema),
});

export type OrganisationUser = z.infer<typeof organisationUserSchema>;

export function useOrganisationUser(organisation: Organisation) {
  const userResponse = useParsedQuery<OrganisationUser>(
    ["lizard-api-v4", "organisations", "users", "me", organisation.uuid],
    () => fetchWithError(`/api/v4/organisations/${organisation.uuid}/users/me`),
    (json: unknown) => organisationUserSchema.parse(json)
  );

  if (userResponse.isSuccess) return userResponse.data;
  return null;
}

export function useUserHasRole(organisation: Organisation, role: UserRole): boolean {
  const organisationUser = useOrganisationUser(organisation);

  return organisationUser !== null && organisationUser.roles.indexOf(role) !== -1;
}
